import NavigationService from '@disco/state-management/NavigationService'
import type {
  Business,
  Contact,
  Recipient,
  RecipientList,
  RecipientListId,
} from 'types/list'
import type { Action, Thunk } from 'types/redux'
import {
  addToList,
  removeFromList,
} from '@disco/state-management/actions/contact-actions'
import api from '@disco/api/server'
import {
  EMAIL_GROUP_CREATE,
  EMAIL_GROUP_CREATE_FAIL,
  EMAIL_GROUP_CREATE_SUCCESS,
  EMAIL_GROUP_DELETE,
  EMAIL_GROUP_DELETE_FAIL,
  EMAIL_GROUP_DELETE_SUCCESS,
  EMAIL_GROUP_GET,
  EMAIL_GROUP_GET_FAIL,
  EMAIL_GROUP_GET_SUCCESS,
  EMAIL_GROUP_UPDATE,
  EMAIL_GROUP_UPDATE_FAIL,
  EMAIL_GROUP_UPDATE_SUCCESS,
  EMAIL_GROUPS_LOAD,
  EMAIL_GROUPS_LOAD_FAIL,
  EMAIL_GROUPS_LOAD_SUCCESS,
  LIST_BUSINESS_FORM_DONE,
  LIST_FORM_CLOSE,
  LIST_FORM_DELETE,
  LIST_FORM_INCLUDE_BUSINESS,
  LIST_FORM_MODIFY_LISTS,
  LIST_FORM_SAVE,
  LIST_FORM_SAVE_ERROR,
  LIST_FORM_SAVE_SUCCESS,
  LIST_FORM_START_MANAGING_LISTS,
  CONTACT_EMAIL_GROUPS_LOAD,
  CONTACT_EMAIL_GROUPS_LOAD_FAIL,
  CONTACT_EMAIL_GROUPS_LOAD_SUCCESS,
  MAILING_LIST_LOAD,
  MAILING_LIST_LOAD_FAIL,
  MAILING_LIST_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { RECIPIENT_GROUP } from '@disco/constants/RecipientTypes'
import type { CheckboxState } from '@disco/state-management/reducers/forms/list'
import {
  getBusinessCustomizedFormContacts,
  getContacts,
  getSelectAllContactIds,
} from '@disco/state-management/selectors/contacts'
import { contactToRecipient } from '@disco/utils/contacts'
import { pushUINotification } from '@disco/utils/notifications'
const EMAIL_GROUPS_CHUNK_SIZE = 4 // TODO: remove chunking after https://disco-team.atlassian.net/browse/DISCO-5139

/**
 * Loads email groups associated with a specific contact.
 * @param contactId - The ID of the contact to load email groups for
 * @returns Thunk that fetches and dispatches contact email groups
 */
export function loadContactEmailGroups(contactId: number): Thunk {
  return dispatch => {
    dispatch({
      type: CONTACT_EMAIL_GROUPS_LOAD,
    })
    api
      .getContactEmailGroups(contactId)
      .then(response => {
        dispatch({
          type: CONTACT_EMAIL_GROUPS_LOAD_SUCCESS,
          contactEmailGroups: response.results,
        })
      })

      .catch(error => {
        dispatch({
          type: CONTACT_EMAIL_GROUPS_LOAD_FAIL,
          error,
        })
      })
  }
}
type RecipientListResult = {
  results: RecipientList[]
  next: string
}

/**
 * Loads all mailing lists from the server if not already loaded.
 * @returns Thunk that fetches and dispatches mailing lists
 */
export function loadMailingLists(): Thunk {
  return (dispatch, getState) => {
    const { emailGroups } = getState()
    if (emailGroups.mailingList?.length) {
      return
    }

    dispatch({
      type: MAILING_LIST_LOAD,
    })
    api
      .getMailingLists()
      .then((result: RecipientListResult) => {
        dispatch({
          type: MAILING_LIST_LOAD_SUCCESS,
          mailingList: result.results,
        })
      })
      .catch(error => {
        dispatch({
          type: MAILING_LIST_LOAD_FAIL,
          error,
        })
      })
  }
}
/**
 * Loads email groups from the server with chunked loading for large datasets.
 * @returns Thunk that fetches email groups and handles pagination
 */
export function loadEmailGroups(): Thunk {
  return (dispatch, getState) => {
    const { emailGroups } = getState()
    if (
      emailGroups?.emailGroups.length &&
      (emailGroups.isFetched || emailGroups.isFetching)
    ) {
      return
    }

    dispatch({
      type: EMAIL_GROUPS_LOAD,
    })
    api
      .getEmailGroups(EMAIL_GROUPS_CHUNK_SIZE)
      .then((result: RecipientListResult) => {
        if (result.next) {
          dispatch(loadEmailGroupsNext(result.results, result.next))
        } else {
          dispatch({
            type: EMAIL_GROUPS_LOAD_SUCCESS,
            emailGroups: result.results,
          })
        }
      })
      .catch(error => {
        dispatch({
          type: EMAIL_GROUPS_LOAD_FAIL,
          error,
        })
      })
  }
}
/**
 * Loads the next chunk of email groups for pagination.
 * @param results - Existing email groups results
 * @param next - URL for the next page of results
 * @returns Thunk that fetches additional email groups
 */
export function loadEmailGroupsNext(
  results: RecipientList[],
  next: string,
): Thunk {
  return async dispatch => {
    try {
      const response = await api.getUrl<any>(
        next,
        null,
        'api.getEmailGroupsNext',
      )
      const updatedResults = [...results, ...response.results]
      if (response.next) {
        return dispatch(loadEmailGroupsNext(updatedResults, response.next))
      } else {
        return dispatch({
          type: EMAIL_GROUPS_LOAD_SUCCESS,
          emailGroups: updatedResults,
        })
      }
    } catch (error) {
      dispatch({
        type: EMAIL_GROUPS_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads a specific email group by ID.
 * @param id - The ID of the email group to load
 * @returns Thunk that fetches and dispatches the email group
 */
export function loadEmailGroup(id: number): Thunk {
  return dispatch => {
    dispatch({
      type: EMAIL_GROUP_GET,
      id,
    })
    api
      .getEmailGroup(id)
      .then(result => {
        dispatch({
          type: EMAIL_GROUP_GET_SUCCESS,
          id,
          emailGroup: result,
        })
      })
      .catch(error => {
        dispatch({
          type: EMAIL_GROUP_GET_FAIL,
          id,
          error,
        })
      })
  }
}
/**
 * Deletes an email group and shows success notification.
 * @param group - The email group to delete
 * @returns Thunk that deletes the group and dispatches results
 */
export function deleteEmailGroup(group: RecipientList): Thunk {
  return dispatch => {
    dispatch({
      type: EMAIL_GROUP_DELETE,
      emailGroup: group,
    })
    api
      .deleteEmailGroup(group)
      .then(() => {
        dispatch({
          type: EMAIL_GROUP_DELETE_SUCCESS,
          emailGroup: group,
          meta: {
            mixpanel: {
              event: 'Delete email group',
              props: {
                Name: group.name,
              },
            },
          },
        })
        pushUINotification({
          message: 'List deleted!',
          level: 'success',
        })
      })
      .catch(error => {
        dispatch({
          type: EMAIL_GROUP_DELETE_FAIL,
          emailGroup: group,
          error,
        })
      })
  }
}

function flattenEmailGroup(group: Partial<RecipientList>) {
  if (!group.recipients) return group
  const extractedRecipients: Recipient[] = []
  group.recipients.forEach(recipient => {
    if (recipient.type === RECIPIENT_GROUP) {
      if (recipient.recipients)
        extractedRecipients.push(...recipient.recipients)
    } else {
      extractedRecipients.push(recipient)
    }
  })
  return { ...group, recipients: extractedRecipients }
}

/**
 * Updates an existing email group with new data.
 * @param group - Partial email group data to update
 * @returns Thunk that updates the group and shows notifications
 */
export function updateEmailGroup(group: Partial<RecipientList>): Thunk {
  return async dispatch => {
    dispatch({
      type: EMAIL_GROUP_UPDATE,
      emailGroup: group,
    })

    try {
      const result = await api.updateEmailGroup(flattenEmailGroup(group))
      dispatch({
        type: EMAIL_GROUP_UPDATE_SUCCESS,
        emailGroup: result,
        meta: {
          mixpanel: {
            event: 'Update email group',
            props: {
              Name: group.name,
            },
          },
        },
      })
      pushUINotification({
        message: 'List edited!',
        level: 'success',
      })
      return result
    } catch (error) {
      dispatch({
        type: EMAIL_GROUP_UPDATE_FAIL,
        emailGroup: group,
        error,
      })
      throw error
    }
  }
}
/**
 * Creates a new email group with the provided data.
 * @param group - Partial email group data for creation
 * @returns Thunk that creates the group and shows notifications
 */
export function createEmailGroup(group: Partial<RecipientList>): Thunk {
  return async dispatch => {
    dispatch({
      type: EMAIL_GROUP_CREATE,
      emailGroup: group,
    })

    try {
      const result = await api.createEmailGroup(flattenEmailGroup(group))
      dispatch({
        type: EMAIL_GROUP_CREATE_SUCCESS,
        emailGroup: result,
        meta: {
          mixpanel: {
            event: 'Create email group',
            props: {
              Name: group.name,
            },
          },
        },
      })
      pushUINotification({
        message: 'List created!',
        level: 'success',
      })
      return result
    } catch (error) {
      dispatch({
        type: EMAIL_GROUP_CREATE_FAIL,
        emailGroup: group,
        error,
      })
      throw error
    }
  }
}

function contactsWithMultipleBusinesses(contacts: Array<Contact>) {
  const multipleBusinesses = {}
  contacts.forEach(contact => {
    if (!contact.disco_user) return
    const user = contact.disco_user
    if (user.businesses.length <= 1) return
    const mapping = {}
    user.businesses.forEach(b => {
      mapping[b.id] = 'none'
    })
    multipleBusinesses[contact.id] = mapping
  })
  return multipleBusinesses
}

/**
 * Starts the list management form with selected contacts and configuration.
 * @param selectedContacts - Array of contacts to manage
 * @param mode - The list management mode
 * @param selectedList - Optional existing list to work with
 * @param options - Optional configuration including redirect URL
 * @param selectedContactsIds - Optional array of contact IDs
 * @returns Thunk that initializes list management form
 */
export function listFormStartManagingLists(
  selectedContacts: Array<Contact>,
  mode: 'MULTIPLE_LISTS' | 'SINGLE_LIST' | 'EXISTING_LIST',
  selectedList?: RecipientList,
  options = {} as { redirectTo?: string },
  selectedContactsIds?: number[],
): Thunk {
  return async dispatch => {
    const multipleBusinesses = contactsWithMultipleBusinesses(selectedContacts)

    if (mode === 'EXISTING_LIST') {
      // ask no more - save
      dispatch({
        type: LIST_FORM_START_MANAGING_LISTS,
        invisible: true,
        // don't show UI - only init form
        selectedContacts:
          selectedContactsIds || selectedContacts.map(c => c.id),
        selectedList: selectedList ? selectedList.id : null,
        multipleBusinesses,
        mode,
        redirectTo: options.redirectTo,
      })
      // submit form
      await dispatch(listFormSave())
      return
    }

    return dispatch({
      type: LIST_FORM_START_MANAGING_LISTS,
      selectedContacts: selectedContactsIds || selectedContacts.map(c => c.id),
      selectedList: selectedList ? selectedList.id : null,
      multipleBusinesses,
      mode,
      redirectTo: options.redirectTo,
    })
  }
}
/**
 * Saves the list form, creating or updating lists as needed.
 * @param groupName - Optional name for a new group to create
 * @returns Thunk that processes form changes and shows notifications
 */
export function listFormSave(groupName?: string | null | undefined): Thunk {
  return async (dispatch, getState) => {
    const {
      forms: { listForm },
    } = getState()
    dispatch({
      type: LIST_FORM_SAVE,
    })

    try {
      let groupJustCreated: RecipientList | null | undefined = null
      const contactsState = getContacts(getState())
      const selectedAll = contactsState?.selectedAll
      const selectedIds = selectedAll
        ? getSelectAllContactIds(getState())
        : undefined
      const contacts = getBusinessCustomizedFormContacts(getState())
      const changes = Object.keys(listForm.listsToChange)
      let updated = 0

      for (const listIdKey of changes) {
        // move to bulk api for changing M groups for N users?
        const listId = parseInt(listIdKey)
        const value = listForm.listsToChange[listId]

        if (value === 'all' || value === 'some-all') {
          await dispatch(addToList(listId, contacts, selectedIds))
          updated++
        } else if (value === 'none' || value === 'some-none') {
          await dispatch(removeFromList(listId, contacts, selectedIds))
          updated++
        }
      }

      if (typeof groupName === 'string') {
        // note: it also dispatches "List created!" pushUINotification
        groupJustCreated = await dispatch(
          createEmailGroup({
            name: groupName,
            recipients: contacts.map(contactToRecipient),
          }),
        )
      }

      // close modal
      dispatch({
        type: LIST_FORM_SAVE_SUCCESS,
      })

      // if group was created - open it
      if (groupJustCreated) {
        NavigationService.push(`/lists/${groupJustCreated.id}`)
      } else if (listForm.redirectTo) {
        NavigationService.push(listForm.redirectTo)
      }

      if (updated > 0) {
        pushUINotification({
          message: `${updated} ${
            updated !== 1 ? 'lists were' : 'list was'
          } updated!`,
          level: 'success',
        })
      } // TODO: success message
    } catch (error) {
      return dispatch({
        type: LIST_FORM_SAVE_ERROR,
        error,
      })
    }
  }
}
/**
 * Creates an action to delete the current list form.
 * @returns Action object with LIST_FORM_DELETE type
 */
export function listFormDelete(): Action {
  return {
    type: LIST_FORM_DELETE,
  }
}
/**
 * Creates an action to close the list form.
 * @returns Action object with LIST_FORM_CLOSE type
 */
export function listFormClose(): Action {
  return {
    type: LIST_FORM_CLOSE,
  }
}
/**
 * Creates an action to indicate the business form is complete.
 * @returns Action object with LIST_BUSINESS_FORM_DONE type
 */
export function listBusinessFormDone(): Action {
  return {
    type: LIST_BUSINESS_FORM_DONE,
  }
}
/**
 * Creates an action to include or exclude a business for a contact in list form.
 * @param contact - The contact to modify
 * @param business - The business to include/exclude
 * @param value - Whether to include ('all') or exclude ('none') the business
 * @returns Action object with contact, business, and inclusion data
 */
export function listFormIncludeBusiness(
  contact: Contact,
  business: Business,
  value: 'all' | 'none',
): {
  type: string
  contactId: number
  businessId: number
  value: 'all' | 'none'
} {
  return {
    type: LIST_FORM_INCLUDE_BUSINESS,
    contactId: contact.id,
    businessId: business.id,
    value: value,
  }
}
/**
 * Creates an action to modify list selection in the form.
 * @param listId - The ID of the list to modify
 * @param selectedValue - The new checkbox state for the list
 * @returns Action object with list modification data
 */
export function listFormModifyLists(
  listId: RecipientListId,
  selectedValue: CheckboxState,
) {
  return {
    type: LIST_FORM_MODIFY_LISTS,
    listId: listId,
    selectedValue: selectedValue,
  }
}
/**
 * Saves contacts to a list after import process.
 * @param listId - The ID of the list to add contacts to
 * @returns Thunk that adds contacts to the list and redirects
 */
export function listFormPostImportSave(listId: RecipientListId): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: LIST_FORM_SAVE,
    })

    try {
      const contacts = getBusinessCustomizedFormContacts(getState())
      await dispatch(addToList(listId, contacts))
      dispatch({
        type: LIST_FORM_SAVE_SUCCESS,
      })
      NavigationService.push(`/lists/${listId}`) // TODO: success message
    } catch (error) {
      return dispatch({
        type: LIST_FORM_SAVE_ERROR,
        error,
      })
    }
  }
}
/**
 * Exports an email group's data for download.
 * @param id - The ID of the email group to export
 * @returns Thunk that exports the group data or shows error notification
 */
export const exportEmailGroup = (id: RecipientListId): Thunk => {
  return async _dispatch => {
    try {
      const response = await api.exportMailingList(id)
      return response
    } catch (_error) {
      pushUINotification({
        message: 'List export error!',
        level: 'error',
      })
    }

    return {
      error: true,
    }
  }
}
